import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useApp } from '../../context/AppContext';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { VerifiedBadge } from '../app/VerifiedBadge';
import { Compass, UserPlus, UserCheck, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

function MiniUser({ u, t }) {
  const navigate = useNavigate();
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  const follow = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/users/${u.id}/follow`);
      setFollowing(res.data.following);
      if (res.data.following) toast.success(`+ @${u.username}`);
    } catch {} finally { setBusy(false); }
  };

  return (
    <div data-testid="suggested-user-item" className="shrink-0 w-32 bg-secondary/40 border border-border rounded-xl p-3 flex flex-col items-center text-center">
      <Avatar className="w-12 h-12 cursor-pointer" onClick={() => navigate(`/profile/${u.id}`)}>
        <AvatarImage src={u.avatar} />
        <AvatarFallback>{u.username?.[0]?.toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex items-center gap-0.5 mt-1.5 max-w-full">
        <p className="text-xs font-semibold truncate">{u.full_name}</p>
        {u.verified && <VerifiedBadge size={11} />}
      </div>
      <p className="text-[10px] text-muted-foreground truncate max-w-full">@{u.username}</p>
      <Button
        data-testid="suggested-follow-button"
        size="sm"
        onClick={follow}
        disabled={busy}
        variant={following ? 'secondary' : 'default'}
        className="mt-2 h-7 text-[11px] w-full font-semibold"
      >
        {following ? <UserCheck size={11} className="me-1" /> : <UserPlus size={11} className="me-1" />}
        {following ? t('unfollow') : t('follow')}
      </Button>
    </div>
  );
}

export default function SuggestedUsers() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get('/users/suggestions').then((r) => setUsers(r.data.slice(0, 8))).catch(() => {});
  }, []);

  if (users.length === 0) return null;

  return (
    <div className="bg-card rounded-xl border border-border p-3 elev-1" data-testid="suggested-users-strip">
      <div className="flex items-center justify-between mb-2.5 px-1">
        <span className="text-xs font-semibold flex items-center gap-1.5">
          <Compass size={14} className="text-primary" /> {t('suggestedForYou')}
        </span>
        <button data-testid="suggested-see-all-button" onClick={() => navigate('/discover')}
          className="text-[11px] text-primary hover:underline flex items-center">
          {t('discover')} <ChevronRight size={12} className="rtl:rotate-180" />
        </button>
      </div>
      <div className="flex gap-2.5 overflow-x-auto pb-1">
        {users.map((u) => <MiniUser key={u.id} u={u} t={t} />)}
      </div>
    </div>
  );
}
